// Render a packet to a PDF Blob and (optionally) upload it to Google Drive.
//
// Private packets are @react-pdf templates. GAPP packets fill the ORIGINAL DCH
// appendix PDFs (gappOverlay.js). Both paths take the same assembled data, and
// the builder/signing previews call renderPacketBlob too — single source.

import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { supabase, updateClient } from '../supabase';
import { CLIENT_DRIVE_ROOT } from '../driveRoots';
import PrivateIntakePacket from './PrivateIntakePacket';
import { renderGappPacket, renderGappDocument, renderGappCertificate, isUploadForm } from './gappOverlay';
import { renderInternalPage } from './GappInternalPages';
import { PACKET_DEFS, DOC_SHORT_NAME, GENERAL_CLIENT_CODE, REQUEST_KINDS } from './packets';
import { fileName } from './fileNames';
import { buildParties } from './packetParties';
import { normalizeLang, formTitle, packetLabel, signerLabel } from './i18n';

// packet_type → @react-pdf component (typeset packets only)
const PACKET_COMPONENTS = { private_intake: PrivateIntakePacket };

export const isGappPacket = (type) => String(type || '').startsWith('gapp_');

// Build the data shape the packet renderers expect from DB rows.
// forms: array of packet_forms rows; slots: array of signature_slots rows.
export function assemblePacketData({ packet, client, forms, slots, tokens, files }) {
  const gapp = isGappPacket(packet.packet_type);
  const lang = normalizeLang(packet.language);
  const formData = {};
  const perForm = {};
  (forms || []).forEach((f) => {
    perForm[f.form_key] = { ...(f.field_data || {}), sourceFileId: f.source_file_id || f.field_data?.sourceFileId, name: f.source_name || f.field_data?.name };
    // Uploaded documents keep their placed fields to themselves; state forms
    // and our own documents share one flat field space as before.
    if (!isUploadForm(f.form_key)) Object.assign(formData, f.field_data || {});
  });

  const titleOf = {};
  (forms || []).forEach((f) => { titleOf[f.form_key] = f.form_title; });

  const signatures = {};
  const slotsMeta = [];
  (slots || []).forEach((s) => {
    const key = s.slot_key || `${s.form_key}_${s.party_role}`;
    signatures[key] = {
      name: s.signed_name || '',
      date: s.signed_at ? fmtDate(s.signed_at) : '',
      dateShort: s.signed_at ? fmtShort(s.signed_at) : '',
      choices: s.choices || {},
    };
    slotsMeta.push({
      slot_key: key, form_key: s.form_key, form_title: formTitle(lang, s.form_key, titleOf[s.form_key]),
      signer_label: signerLabel(lang, key, s.signer_label), party_role: s.party_role,
      signed_name: s.signed_name || '', signed_at: s.signed_at || null, signed_by: s.signed_by || null,
      signed_ip: s.signed_ip || null, signed_user_agent: s.signed_user_agent || null,
    });
  });

  const addr = [client.address, client.city, client.state, client.zip].filter(Boolean).join(', ');
  const guardianFullName  = [client.guardian_first_name, client.guardian_last_name].filter(Boolean).join(' ');
  const emergencyFullName = [client.emergency_first_name, client.emergency_last_name].filter(Boolean).join(' ');
  const stamp = packet.completed_at || new Date().toISOString();
  return {
    lang,
    date: fmtDate(stamp),
    dateShort: fmtShort(stamp),
    packet: {
      id: packet.id, type: packet.packet_type,
      label: packet.title || packetLabel(lang, packet.packet_type, PACKET_DEFS[packet.packet_type]?.label),
      kind: packet.request_kind || null,
      createdAt: packet.created_at, completedAt: packet.completed_at,
    },
    client: {
      name: [client.first_name, client.last_name].filter(Boolean).join(' '),
      id: client.client_id,
      // State forms print dates as MM/DD/YYYY; the typeset private packet keeps the long form.
      dob: gapp ? toUS(client.date_of_birth) : (client.date_of_birth || ''),
      age: ageFrom(client.date_of_birth),
      // A blank Medicaid line must stay blank on a state form.
      medicaid: client.medicaid_id || (gapp ? '' : 'N/A — Private Pay'),
      guardian: guardianFullName || client.responsible_person_name || '',
      coordinator: packet.prefilled_data?.coordinator || '',
      phone: client.guardian_phone || client.emergency_phone || '',
      email: client.guardian_email || client.emergency_email || '',
      address: addr,
      relationship: client.guardian_relationship || '',
      // Extended record fields for the Client Information section (HFRD .09(4)(a))
      gender: client.gender || '',
      language: client.language || '',
      responsibleParty: guardianFullName || client.responsible_person_name || '',
      responsiblePhone: client.guardian_phone || client.emergency_phone || '',
      responsibleRelationship: client.guardian_relationship || client.emergency_relationship || '',
      emergencyName: emergencyFullName || '',
      emergencyPhone: client.emergency_phone || '',
      emergencyRelationship: client.emergency_relationship || '',
      physician: client.doctor_name || '',
      physicianPhone: client.doctor_phone || '',
      physicianAddress: client.doctor_address || '',
      diagnosis: client.diagnosis_summary || '',
      allergies: client.allergies || '',
      medications: client.current_medications || '',
      referralDate: client.referral_date || '',
      referralSource: client.referral_source || '',
      insurance: client.insurance_name || (client.medicaid_id ? 'Medicaid' : 'Private Pay'),
      mfmcStatus: client.mfmc_status || '',
    },
    forms: formData,
    formData: perForm,
    files: files || {},
    formKeys: (forms || []).map((f) => f.form_key),
    signatures,
    slotsMeta,
    parties: buildParties(slotsMeta, tokens || []),
  };
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
}

// MM/DD/YYYY for state forms. Accepts ISO timestamps and YYYY-MM-DD (kept in local
// calendar terms so a DOB does not slip a day across time zones).
export function toUS(v) {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-US');
}
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US') : '');

function ageFrom(dob) {
  const m = String(dob || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const b = new Date(+m[1], +m[2] - 1, +m[3]);
  const now = new Date();
  let years = now.getFullYear() - b.getFullYear();
  const months = now.getMonth() - b.getMonth() - (now.getDate() < b.getDate() ? 1 : 0);
  if (months < 0) years -= 1;
  if (years < 2) {
    const totalMonths = years * 12 + ((months + 12) % 12);
    return `${totalMonths} months`;
  }
  return `${years}`;
}

// Render already-assembled data (used by the dev preview with sample data).
export async function renderPacketBlobFromData(packetType, data) {
  if (isGappPacket(packetType)) {
    const keys = data.formKeys?.length ? data.formKeys : (PACKET_DEFS[packetType]?.forms || []).map((f) => f.form_key);
    const bytes = await renderGappPacket(data, keys, { renderInternal: (kind) => renderInternalPage(kind, data) });
    return new Blob([bytes], { type: 'application/pdf' });
  }
  const Component = PACKET_COMPONENTS[packetType];
  if (!Component) throw new Error(`No PDF template for packet type: ${packetType}`);
  return pdf(<Component data={data} />).toBlob();
}

// Render a packet (DB rows) to a Blob.
export async function renderPacketBlob({ packet, client, forms, slots, tokens, files }) {
  const data = assemblePacketData({ packet, client, forms, slots, tokens, files });
  return renderPacketBlobFromData(packet.packet_type, data);
}

// One Blob per document of a GAPP request, in request order, plus the
// certificate. Each is what gets filed; nothing is merged.
export async function renderDocumentBlobs({ packet, client, forms, slots, tokens, files }) {
  const data = assemblePacketData({ packet, client, forms, slots, tokens, files });
  const opts = { renderInternal: (kind) => renderInternalPage(kind, data) };
  const docs = [];
  for (const f of forms || []) {
    const bytes = await renderGappDocument(data, f.form_key, opts);
    if (bytes) docs.push({ form: f, blob: new Blob([bytes], { type: 'application/pdf' }) });
  }
  const cert = await renderGappCertificate(data, opts);
  return { docs, certificate: cert ? new Blob([cert], { type: 'application/pdf' }) : null };
}

async function uploadBlob({ blob, filename, client, subfolder }) {
  const { data, error } = await supabase.functions.invoke('upload-to-drive', {
    body: {
      fileBase64: await blobToBase64(blob), filename,
      clientId: client.client_id, rootFolderId: CLIENT_DRIVE_ROOT,
      subfolder, contentType: 'application/pdf',
    },
  });
  if (error || !data?.success) throw new Error(error?.message || data?.error || 'Drive upload failed');
  if (client.id && !client.drive_folder_id && data.clientFolderId) {
    Promise.resolve(updateClient(client.id, { drive_folder_id: data.clientFolderId })).catch(() => {});
  }
  return { fileId: data.fileId, webViewLink: data.webViewLink, folderId: data.subFolderId };
}

// What a request is filed under: the G-number, or for a client-less request
// its subject. Shared by the documents and the certificate.
export function requestFilePrefix({ client, packet }) {
  return client.client_id === GENERAL_CLIENT_CODE ? String(packet?.subject || packet?.recipient_name || 'General') : client.client_id;
}

// File name for one document of a request: "G005_2026-09_Appendix_H_signed.pdf"
// (GAMMIS-safe — see fileNames.js).
export function documentFileName({ client, form, slots, packet }) {
  const ym = (packet?.completed_at || new Date().toISOString()).slice(0, 7);
  const short = isUploadForm(form.form_key)
    ? String(form.source_name || form.form_title || 'Document').replace(/\.pdf$/i, '')
    : (DOC_SHORT_NAME[form.form_key] || form.form_title || form.form_key);
  const signed = (slots || []).some((s) => s.form_key === form.form_key && s.signed_at);
  return fileName([requestFilePrefix({ client, packet }), ym, short, signed ? 'signed' : '']);
}

// "G005_2026-09_Signature_certificate_Family_forms_renewal.pdf". Two requests
// can file into the same folder (the family forms and the LON of one
// submission), so the certificate carries the request's name.
export function certificateFileName({ client, packet }) {
  const ym = (packet?.completed_at || new Date().toISOString()).slice(0, 7);
  const which = REQUEST_KINDS[packet?.request_kind]?.label || packet?.title || '';
  return fileName([requestFilePrefix({ client, packet }), ym, 'Signature certificate', which]);
}

// Render every document of a GAPP request and file each as its own PDF in
// the request's folder (the PA cycle's "PA Submissions/<label>", or
// "Signing Packets" when the request has no cycle), then the certificate.
// onProgress(formKey | 'certificate', { fileId, webViewLink }) fires as each
// lands, so the caller can record it even if a later upload fails.
export async function fileRequestToDrive({ packet, client, forms, slots, tokens, files, subfolder, onProgress }) {
  const target = subfolder || 'Signing Packets';
  const { docs, certificate } = await renderDocumentBlobs({ packet, client, forms, slots, tokens, files });
  const filed = [];
  let folderId = null;
  for (const { form, blob } of docs) {
    const r = await uploadBlob({ blob, filename: documentFileName({ client, form, slots, packet }), client, subfolder: target });
    folderId = r.folderId || folderId;
    filed.push({ form, ...r });
    if (onProgress) await onProgress(form.form_key, r);
  }
  let cert = null;
  if (certificate) {
    cert = await uploadBlob({ blob: certificate, filename: certificateFileName({ client, packet }), client, subfolder: target });
    folderId = cert.folderId || folderId;
    if (onProgress) await onProgress('certificate', cert);
  }
  return { filed, certificate: cert, folderId };
}

// Private-track packets are still one typeset PDF (the private intake is a
// single document by design).
export async function generateAndUploadPacket({ packet, client, forms, slots, tokens, rootFolderId, subfolder }) {
  const blob = await renderPacketBlob({ packet, client, forms, slots, tokens });
  const safeName = `${(client.last_name || 'Client')}_${packet.packet_type}_${Date.now()}.pdf`;
  const r = await uploadBlob({ blob, filename: safeName, client, subfolder: subfolder || 'Signing Packets' });
  return { fileId: r.fileId, webViewLink: r.webViewLink };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
